import { useEffect, useRef } from 'react'
import { datadogRum } from '@datadog/browser-rum'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import {
  refreshOrderConfirmation as refreshOrderConfirmationAction,
  refreshOrderDetail as refreshOrderDetailAction,
} from '../../redux/slices/orders'
import useAppDispatch from '../useAppDispatch'
import useAppSelector from '../useAppSelector'
import useGetOrders from './useGetOrders'
import getGoToChannelUrl, {
  isSupportedChannel,
} from '../../utils/common/getGoToChannelUrl'
import { generateDataDogContext } from '../../utils/dataDog'
import { WorkflowChannel } from '../../graphQL/queries/getSession/types'
import { readString, ScalarActionArgs } from '../../utils/actionArgs'
import { reportOrdersBackgroundError } from './ordersHookHelpers'

/**
 * Query param carrying the selected status tab.
 *
 * Reading it lets a deep link — a bot message pointing at delivered orders,
 * say — open the screen with that tab already selected. Writing it back on
 * every tab change keeps a page reload and the browser back button honest.
 * @internal
 */
export const ORDERS_STATUS_PARAM = 'status'

/**
 * Location-state key `openOrderDetail` sets on the detail entry it pushes.
 *
 * Its presence tells `backToHistory` that the entry behind the detail is the
 * screen that dispatched `openOrderDetail` (normally the list), so going back
 * can *pop* to it rather than push a copy of the list. See `backToHistory` for
 * why that matters.
 * @internal
 */
export const FROM_ORDER_HISTORY_STATE_KEY = 'fromOrderHistory'

/**
 * How long a pending pop may take before a further back tap stops being read
 * as a double tap and is read as the pop having failed. Inside it a tap is
 * dropped; past it the tap is reported and falls back to the replace.
 * @internal
 */
export const POP_PENDING_WINDOW_MS = 1000

/**
 * RUM action name for a replace taken with history behind the detail: an
 * entry with no marker that was not a deep link, such as a config `pushView`
 * onto the detail route. Each one leaves a duplicate list in the stack. The
 * stuck-pop fallback sends it too, with `hasMarker: true`, so split on that
 * field before reading the count as `pushView` traffic.
 * @internal
 */
export const ORDER_DETAIL_BACK_REPLACE_ACTION = 'order_detail_back_replace'

/**
 * One field of a history or location state, or `undefined` when the state is
 * not an object. Both are `unknown` at runtime: the router writes its own
 * shape, and anything else may have written the entry.
 */
const readStateField = (state: unknown, key: string): unknown => {
  if (typeof state !== 'object' || state === null) {
    return undefined
  }
  return (state as Record<string, unknown>)[key]
}

/**
 * The router's position in the session history, read at call time.
 *
 * react-router 6.28 writes `{ usr, key, idx }` into every entry it creates,
 * and `idx` is 0 on the entry the app booted on. 0 as well when the entry
 * carries none, which is treated as nothing behind it.
 */
const readHistoryIdx = (): number => {
  const idx = readStateField(window.history.state, 'idx')
  return typeof idx === 'number' ? idx : 0
}

/** True when a location's state carries the `openOrderDetail` marker. */
const isFromOrderHistory = (state: unknown): boolean =>
  readStateField(state, FROM_ORDER_HISTORY_STATE_KEY) === true

interface OpenOrderDetailArgs extends ScalarActionArgs {
  orderId?: unknown
}

/**
 * The EventManager actions the Order History and Order Detail screens
 * dispatch.
 *
 * Rendering lives entirely in SCS config, so these are the only seams between
 * the authored component tree and the orders data layer.
 */
export const useOrderActions = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [searchParams, setSearchParams] = useSearchParams()
  // Read through a ref so a handler reads the location as of the call, not as
  // of the render that registered it: `EventManager` re-registers on store
  // changes, not on navigation, so a closed-over `location` can be stale.
  const location = useLocation()
  const locationRef = useRef(location)
  locationRef.current = location
  // When a pop-branch `backToHistory` called `navigate(-1)`, until that pop
  // lands; `null` when none is pending. See the guard in `backToHistory`.
  // Four resets, one per way the pop can end: it unmounts this screen (the
  // ref dies with this instance); it lands on a location this screen also
  // renders (the location-key effect below); it leaves the document and the
  // page later comes back from the back-forward cache (the `pageshow`
  // effect); or it is judged failed past the window, reported, and cleared
  // in `backToHistory` itself. That last one is the only reset on the
  // no-slug path, where the fallback replace cannot navigate and so no
  // location change follows.
  const popPendingSinceRef = useRef<number | null>(null)
  const slug = useAppSelector((state) => state.defaultSlice.storeName)
  const sessionId = useAppSelector((state) => state.defaultSlice.sessionId)
  const activeFilter = useAppSelector((state) => state.ordersSlice.activeFilter)
  const workflow = useAppSelector(
    (state) => state.defaultSlice.sessionData?.workflow,
  )
  const configPhoneNumber = useAppSelector(
    (state) => state.defaultSlice.config?.phoneNumber,
  )
  const { loadMoreOrders, changeOrdersFilter } = useGetOrders()

  // A pop that lands on a route this screen also renders keeps this instance
  // mounted: detail B popping to detail A, when a detail's own container
  // authors `openOrderDetail`. Every later back tap on A would be dropped. Any
  // location change means the pending pop has landed, so clear on that. Both
  // taps of a double tap arrive before the location changes, so the guard
  // still holds for them. `key`, not `pathname`: a pop between two entries of
  // the same path (the same order opened from its own screen) changes only
  // the key.
  useEffect(() => {
    popPendingSinceRef.current = null
  }, [location.key])

  // A pop whose target belongs to another document (the detail was reloaded)
  // loads a new page instead of changing this one's location, and a later
  // back-forward-cache restore brings this page back with the flag still set
  // and no location change to clear it. A restore is the only `pageshow` with
  // `persisted`, so that is the reset.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        popPendingSinceRef.current = null
      }
    }
    window.addEventListener('pageshow', onPageShow)
    return () => {
      window.removeEventListener('pageshow', onPageShow)
    }
  }, [])

  /**
   * Row tap. Accepts either `{{this.value}}` (OrderList calls back with the
   * bare id) or an explicit `orderId` arg, so a storefront can author either.
   */
  const openOrderDetail = (args: OpenOrderDetailArgs = {}) => {
    const orderId = readString(args.orderId, args.value)
    // A row with no id cannot be routed to. Navigating to `/orders/` would
    // fall through the wildcard to NotFound, which reads as a crash.
    if (!orderId || !slug || !sessionId) return

    // The marker is what lets `backToHistory` pop back to the entry of the
    // screen dispatching this (normally the list) instead of pushing a list.
    navigate(`/${slug}/${sessionId}/orders/${orderId}`, {
      state: { [FROM_ORDER_HISTORY_STATE_KEY]: true },
    })
  }

  /**
   * Load-more sentinel. The re-entrancy and has-next-page guards live in
   * `useGetOrders`, so this stays a pass-through.
   */
  const loadMore = () => loadMoreOrders()

  /**
   * Status tab. An empty string is the "All" tab, which drops the param
   * rather than writing `?status=`.
   */
  const filterOrders = (args: ScalarActionArgs = {}) => {
    const status = readString(args.value)

    const nextParams = new URLSearchParams(searchParams)
    if (status) {
      nextParams.set(ORDERS_STATUS_PARAM, status)
    } else {
      nextParams.delete(ORDERS_STATUS_PARAM)
    }
    // `replace` so a run of tab taps leaves one history entry, not one per
    // tap for the back button to walk through.
    setSearchParams(nextParams, { replace: true })

    return changeOrdersFilter(status)
  }

  /**
   * Back from Order Detail to Order History, for any authored control
   * emitting `templateActions.orders.backToHistory` (typically the header back
   * and the panel's back link).
   *
   * Never a plain push. `openOrderDetail` pushes the detail on top of the
   * list, so pushing the list again left catalog → list → detail → list, and
   * the list's own back (`goBackDynamic`, which is `navigate(-1)` when there is
   * history behind it) popped to the detail: the two screens bounced between
   * each other and the shopper could not leave (COM-1190). So, two branches:
   *
   * - **Pop** when the location carries the `FROM_ORDER_HISTORY_STATE_KEY`
   *   marker *and* the entry has history behind it (`idx > 0`):
   *   `navigate(-1)`. The entry behind is the screen that dispatched
   *   `openOrderDetail`, normally the list, and popping reuses it — `?status`
   *   included, since `filterOrders` writes the tab onto that entry with
   *   `replace`.
   * - **Replace** otherwise (a deep link, an authored `pushView`, a marked
   *   entry with nothing behind it): replace the detail entry with the list.
   *   The status tab is rebuilt from `activeFilter` rather than from the
   *   current URL: the detail route carries no `?status`, so a bare `/orders`
   *   would remount History, which reads the param once on mount and would
   *   refetch unfiltered — silently dropping the tab the shopper chose.
   *
   * Navigation only. The detail half of the slice is reset by the Order Detail
   * screen's own unmount cleanup (`useOrderDetail`), which runs on either
   * branch and also on the device's or browser's own back, which never
   * reaches this handler.
   */
  const backToHistory = () => {
    const idx = readHistoryIdx()
    const hasHistoryBehind = idx > 0
    const hasMarker = isFromOrderHistory(locationRef.current.state)

    if (hasMarker && hasHistoryBehind) {
      const pendingSince = popPendingSinceRef.current
      if (pendingSince === null) {
        popPendingSinceRef.current = Date.now()
        navigate(-1)
        return
      }

      // A double tap (or two authored back controls in quick succession)
      // reaches here again before the first pop lands, with the marker still
      // on the location. A second `go(-1)` before the first traversal
      // completes overshoots the list to the catalog — observed in headless
      // Chrome — so a tap inside the window is dropped.
      const pendingMs = Date.now() - pendingSince
      if (pendingMs < POP_PENDING_WINDOW_MS) {
        return
      }

      // Past the window the pop has failed rather than being slow, and every
      // later tap would be dropped with it. Report it and replace instead.
      // Read `pendingMs` as a distribution: on a slow device a pop slower
      // than the window is a false positive. The fallback is safe either way,
      // because it only replaces and never calls `go` again. No `startView`:
      // the replace below is itself a navigation, so starting a view here
      // would add a second one on every stuck pop.
      reportOrdersBackgroundError(
        new Error('back pop did not land'),
        'backToHistory',
        'order detail back pop did not land, replacing',
        { idx, pendingMs },
      )
      popPendingSinceRef.current = null
    }

    // `navigate(-1)` needs neither; the replace builds a path from both.
    // Unlike the stuck pop above, this keeps `startView`, as
    // `returnToConversation`'s no-phone report does: the control the shopper
    // tapped does nothing and no navigation follows to mark the moment.
    if (!slug || !sessionId) {
      const context = generateDataDogContext({
        title: 'order detail back had no slug or session',
        extraInfo: {
          function: 'backToHistory',
          hasSlug: !!slug,
          hasSessionId: !!sessionId,
        },
      })
      datadogRum.startView(context.viewName)
      datadogRum.addError(new Error('no slug or sessionId'), context)
      return
    }

    // History behind the detail but no usable marker: not a deep link, so the
    // replace leaves a duplicate list in the stack. Counted so a storefront
    // opening the detail through `pushView` shows up. `idx` 0 is a genuine
    // deep link and says nothing. The stuck-pop fallback above also lands
    // here, with `hasMarker: true`.
    if (hasHistoryBehind) {
      datadogRum.addAction(ORDER_DETAIL_BACK_REPLACE_ACTION, { idx, hasMarker })
    }

    const params = new URLSearchParams()
    if (activeFilter) {
      params.set(ORDERS_STATUS_PARAM, activeFilter)
    }
    const query = params.toString()

    navigate(`/${slug}/${sessionId}/orders${query ? `?${query}` : ''}`, {
      replace: true,
    })
  }

  /**
   * Re-read the order on the Detail screen. The retry for `error` and
   * `notFound`, and the only way to *see the order* from `timeout`, where the
   * panel hides the body: the header's back control is drawn in every state,
   * so leaving was never the problem — re-reading was. Before COM-779 the
   * shopper's only route to the order itself was to leave and open it again.
   *
   * A bare dispatch, not a call into the poll's own hook. Calling
   * `useGetOrderDetail().fetchOrderDetail` from here would fetch through a
   * *second* instance of that hook, which owns its own `pollTimerRef` and
   * `requestIdRef`: it could neither cancel the live poll nor be cancelled by
   * it, so a refresh would leave two chains writing `selected` on separate
   * timers. The nonce crosses to the instance that owns the timer; a ref
   * cannot be reached from here at all.
   *
   * No guard on there being an order to refresh. This hook cannot see one —
   * the route id lives on the Detail screen — and the guard belongs with the
   * instance that would act on the edge, which is where it is.
   */
  const refreshOrderDetail = () => {
    dispatch(refreshOrderDetailAction())
  }

  /**
   * Re-read the outcome on the Confirmation screen, and the only way forward
   * from `unknown` (COM-930).
   *
   * That state is what the shopper is left with when the poll spends its
   * budget: the order exists, its confirmation was never observed, and the
   * screen's other control leaves for the conversation and discards it. The
   * shipped `unknown` copy tells them to "check again in a moment", which is
   * the one thing this screen could not offer.
   *
   * A bare dispatch. Why it cannot be a call into `useOrderConfirmation` from
   * here, and why the slice is the only place the two halves can meet, is in
   * `confirmationRefreshNonce` (`redux/slices/orders/types.ts`).
   *
   * No guard on there being an order to re-read, or on the status being the
   * one that offers this. Both are decided where the request is made, and
   * putting a copy here would be two places to keep in step.
   */
  const refreshOrderConfirmation = () => {
    dispatch(refreshOrderConfirmationAction())
  }

  /**
   * Leaves the webview for the conversation the shopper arrived from.
   *
   * The exit from the confirmation screen, and required on every terminal
   * state of it — an order is placed by then, so a screen with no way out is
   * a dead end with the shopper's money already spent.
   *
   * Resolution is `getGoToChannelUrl`'s and the two fallbacks are the error
   * route's, deliberately: `sessionData.workflow.channelUid` first, the
   * storefront's configured `phoneNumber` second, WhatsApp as the channel when
   * the session names none. Reused rather than rebuilt (M1-B D-4); there were
   * already two ways to construct this URL in the repo and a third would be
   * one more thing to keep in step.
   *
   * One divergence from the route it mirrors: that one passes
   * `text: botMessage`, which seeds the conversation and restarts the bot.
   * This passes none, so the channel opens with an empty message — the
   * shopper's order is already placed and there is nothing to restart.
   *
   * A missing phone number leaves the shopper where they are rather than
   * navigating to a broken URL. The panel cannot know that happened: its
   * `data-exit="unavailable"` marker fires on `!inFlight && !showsReturn`,
   * i.e. only when the *label* is missing on a terminal state — so a
   * configured label with no phone number draws the button and publishes
   * nothing. That is exactly why the no-op is reported rather than merely
   * returned. A gap worth naming in a
   * comment is a gap worth one line of telemetry: this is the shopper's only
   * exit from a screen where their money has already moved, and without the
   * report the symptom is "the button does nothing" with no trace anywhere.
   */
  const returnToConversation = () => {
    const phoneNumber = workflow?.channelUid || configPhoneNumber
    if (!phoneNumber) {
      const context = generateDataDogContext({
        title: 'return to conversation had no channel or phone number',
        extraInfo: {
          function: 'returnToConversation',
          hasChannelUid: !!workflow?.channelUid,
          hasConfigPhoneNumber: !!configPhoneNumber,
        },
      })
      datadogRum.startView(context.viewName)
      datadogRum.addError(
        new Error('no channelUid and no configured phoneNumber'),
        context,
      )
      return
    }

    const channel = workflow?.channel || WorkflowChannel.WHATSAPP
    // Reported here rather than inside `getGoToChannelUrl`, which is also
    // called from a render body — see that util's note. This is a click, so
    // one report is one shopper hitting the fallback.
    if (!isSupportedChannel(channel)) {
      const context = generateDataDogContext({
        title: 'unsupported workflow channel, falling back to whatsapp',
        extraInfo: { function: 'returnToConversation', channel },
      })
      datadogRum.startView(context.viewName)
      datadogRum.addError(new Error('unsupported workflow channel'), context)
    }

    window.location.href = getGoToChannelUrl({ channel, phoneNumber })
  }

  return {
    returnToConversation,
    openOrderDetail,
    loadMore,
    filterOrders,
    backToHistory,
    refreshOrderDetail,
    refreshOrderConfirmation,
  }
}

export default useOrderActions
